import { BoxIcon } from "@xola/icons";
import { Popover } from "@xola/ui-kit";
import clsx from "clsx";
import { sumBy } from "lodash";
import React from "react";
import { PurchasedUnit, Unit } from "types";
import { useResource } from "../../hooks/useResource";
import { NoTranslate } from "../Common/NoTranslate";

interface AddonDisplayProps {
    purchasedAddon: PurchasedUnit[];
    className?: string;
}

export const AddonDisplay = ({ purchasedAddon = [], className }: AddonDisplayProps) => {
    const addonCount = sumBy(purchasedAddon, "count") ?? 0;

    if (addonCount === 0) {
        return null;
    }

    return (
        <span className={clsx("ui-dashboard-addons", className)}>
            {addonCount > 0 ? (
                <Popover delay={0} duration={[300, 250]} offset={[0, 10]} placement="bottom" trigger="mouseenter">
                    <span className="flex items-center space-x-1">
                        <BoxIcon />
                        <span>{addonCount}</span>
                    </span>

                    <Popover.Content className="space-y-2 p-4">
                        <AddonList count={addonCount} purchasedAddon={purchasedAddon} />
                    </Popover.Content>
                </Popover>
            ) : null}
        </span>
    );
};

interface AddonListProps {
    count: number;
    purchasedAddon: PurchasedUnit[];
}

const AddonList = React.memo(({ count = 0, purchasedAddon }: AddonListProps) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-x-1 font-bold">
                <div>Addons</div>
                <NoTranslate
                    as="div"
                    className="inline-flex h-4 w-4 justify-center rounded-full bg-gray-lighter text-sm"
                >
                    {count}
                </NoTranslate>
            </div>

            <div className="space-y-1" data-testid="addons-display-list">
                {purchasedAddon
                    .filter(({ count }) => count > 0)
                    .map((purchasedAddonUnit) => (
                        <AddonItem key={purchasedAddonUnit.id} purchasedAddonUnit={purchasedAddonUnit} />
                    ))}
            </div>
        </div>
    );
});

interface AddonItemProps {
    purchasedAddonUnit: PurchasedUnit;
}

const AddonItem = ({ purchasedAddonUnit }: AddonItemProps) => {
    const { data: unit } = useResource<Unit>(["units", purchasedAddonUnit.id], null, {
        enabled: !!purchasedAddonUnit.id && !purchasedAddonUnit.name,
    });

    const unitName = unit?.name ?? purchasedAddonUnit.name ?? "";

    return (
        <NoTranslate key={purchasedAddonUnit.id} as="div">
            {purchasedAddonUnit.count} {unitName}
        </NoTranslate>
    );
};
